// Sprint 3.2 — Página /treinamento em web-intake.
//
// Renderiza POL-02 (Treinamento sobre o Canal de Denúncias) — conteúdo
// obrigatório por Lei 14.457/2022 art. 23 (canal com programa de integridade
// efetivo). Rota pública sem auth — qualquer denunciante / colaborador /
// fornecedor pode acessar.
//
// Texto vive aqui como JSX estruturado (paridade PrivacidadePage). Bump
// editorial = atualizar este componente. Sem registro server-side de
// "leu o treinamento" para denunciante anônimo — anonimato técnico não
// quebra. Modelo: o aceite de LGP-02 no IntakeWizard (Sprint 3.1 + 3.8)
// é o gate formal; este treinamento é informativo livre.

import { useTranslation } from 'react-i18next';

const POL_02_LAST_UPDATED = '2026-05-27';

interface Props {
  onBack?: () => void;
}

interface SectionDef {
  id: string;
  title: string;
}

const SECTIONS: ReadonlyArray<SectionDef> = [
  { id: 'o-que-e', title: '1. O que é o Canal de Denúncias' },
  { id: 'o-que-denunciar', title: '2. O que pode ser denunciado' },
  { id: 'anonimato', title: '3. Como funciona o anonimato' },
  { id: 'como-denunciar', title: '4. Como denunciar — passo a passo' },
  { id: 'depois', title: '5. O que acontece depois que você denuncia' },
  { id: 'recurso', title: '6. Seu direito de recurso' },
  { id: 'retaliacao', title: '7. Proteção contra retaliação' },
  { id: 'confidencialidade', title: '8. Confidencialidade' },
  { id: 'faq', title: '9. Perguntas frequentes' },
];

export function TreinamentoPage({ onBack }: Props): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="fade-in privacy-page">
      <article className="card">
        {onBack && (
          <button
            type="button"
            className="footer-link"
            onClick={onBack}
            style={{ marginBottom: '1rem', display: 'inline-block' }}
          >
            {t('treinamento.back_to_intake')}
          </button>
        )}

        <h2>{t('treinamento.title')}</h2>
        <p style={{ color: 'var(--color-text-secondary)' }}>
          {t('treinamento.last_updated', { date: POL_02_LAST_UPDATED })}
        </p>

        <div
          role="alert"
          style={{
            padding: '0.75rem 1rem',
            margin: '1rem 0',
            background: '#fef3c7',
            color: '#78350f',
            borderRadius: '0.375rem',
            fontSize: '0.875rem',
          }}
        >
          {t('treinamento.draft_warning')}
        </div>

        <p style={{ fontStyle: 'italic', color: 'var(--color-text-secondary)' }}>
          {t('treinamento.reading_time')}
        </p>

        <nav aria-label={t('treinamento.toc_label')} className="privacy-toc">
          <ul>
            {SECTIONS.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`}>{s.title}</a>
              </li>
            ))}
          </ul>
        </nav>

        <section className="privacy-section">
          <h3>Por que este treinamento existe</h3>
          <p>
            A Contato Facilites mantém um Canal de Denúncias para receber relatos sobre situações
            que possam violar leis, nossas políticas internas ou nosso código de conduta. Para que o
            canal funcione bem, você precisa saber como usá-lo, o que esperar dele e quais são seus
            direitos ao denunciar.
          </p>
        </section>

        <section id="o-que-e" className="privacy-section">
          <h3>1. O que é o Canal de Denúncias</h3>
          <p>
            É um sistema online, acessível pelo link divulgado pelo RH, onde qualquer pessoa pode
            relatar uma situação que considere irregular. O canal está disponível{' '}
            <strong>24 horas por dia, 7 dias por semana</strong>.
          </p>
          <p>Você pode usar o canal sendo:</p>
          <ul>
            <li>Colaborador Contato Facilites (CLT, PJ, estagiário, jovem aprendiz);</li>
            <li>Fornecedor ou prestador de serviço;</li>
            <li>Cliente;</li>
            <li>
              Público em geral — qualquer pessoa que tenha presenciado ou tomado conhecimento de uma
              irregularidade.
            </li>
          </ul>
        </section>

        <section id="o-que-denunciar" className="privacy-section">
          <h3>2. O que pode ser denunciado</h3>
          <ul>
            <li>
              <strong>Assédio moral ou sexual</strong> — incluindo violência psicológica,
              isolamento, humilhação.
            </li>
            <li>
              <strong>Discriminação</strong> — por raça, gênero, orientação sexual, religião, idade,
              deficiência, origem.
            </li>
            <li>
              <strong>Corrupção, fraude ou desvio financeiro</strong> — pagamentos indevidos,
              conluio, manipulação de contratos.
            </li>
            <li>
              <strong>Conflito de interesses</strong> — gestor contratando familiar, fornecedor
              sócio oculto.
            </li>
            <li>
              <strong>Violação de segurança da informação</strong> — vazamento de dados, acesso
              indevido.
            </li>
            <li>
              <strong>Violação ambiental ou de segurança do trabalho</strong> — descarte irregular,
              exposição a risco.
            </li>
            <li>
              <strong>Violação de leis aplicáveis</strong> — LGPD, anticorrupção, trabalhistas,
              fiscais.
            </li>
            <li>
              <strong>Retaliação</strong> contra denunciante ou testemunha.
            </li>
            <li>
              <strong>Trabalho infantil ou análogo ao escravo</strong> — em qualquer ponto da
              operação ou da cadeia de fornecedores.
            </li>
            <li>
              <strong>Pressão para desvio de conduta</strong> — pedido ou indução para descumprir
              norma, política ou controle interno.
            </li>
          </ul>
          <h4 style={{ marginTop: '1rem', fontSize: '1rem' }}>Quando não usar o canal</h4>
          <ul>
            <li>
              Para questões operacionais cotidianas — fale com seu gestor ou com o RH (Fabiana
              Carvalho).
            </li>
            <li>Para reclamação salarial individual — procure o RH diretamente.</li>
            <li>
              Para emergência médica ou risco iminente — ligue para 192 (SAMU) ou contate o SESMT.
            </li>
            <li>Para dúvidas sobre benefícios ou folha — RH atende diretamente.</li>
          </ul>
          <p style={{ fontStyle: 'italic' }}>
            Em caso de dúvida, denuncie. O comitê fará a triagem e, se não for caso para o canal,
            orientará o caminho correto.
          </p>
        </section>

        <section id="anonimato" className="privacy-section">
          <h3>3. Como funciona o anonimato</h3>
          <p>
            <strong>
              O canal foi construído de modo que o sistema técnico não consegue identificar você.
            </strong>{' '}
            Isto não é uma promessa, é uma garantia técnica:
          </p>
          <ul>
            <li>
              O sistema não armazena seu IP, dispositivo, navegador, localização ou qualquer dado
              que permita rastrear você.
            </li>
            <li>Nem mesmo a equipe técnica da empresa tem como descobrir quem fez uma denúncia.</li>
            <li>
              A única forma de associar você a uma denúncia é se você decidir se identificar
              voluntariamente, ao preencher seu nome ou e-mail no formulário.
            </li>
          </ul>
          <p>
            <strong>Você decide o grau de identificação.</strong> No modo anônimo (padrão), você
            recebe apenas um protocolo e uma senha para acompanhar o caso. Ninguém — nem a empresa,
            nem o comitê — pode descobrir quem você é.
          </p>
          <p>
            <strong>Guarde o protocolo e a senha em local seguro.</strong> Eles são a única forma de
            acompanhar o caso e enviar informações adicionais. A empresa não tem como recuperá-los
            para você — isso faz parte da garantia de anonimato.
          </p>
        </section>

        <section id="como-denunciar" className="privacy-section">
          <h3>4. Como denunciar — passo a passo</h3>
          <ol>
            <li>Abra o canal pelo link divulgado pelo RH, de qualquer dispositivo.</li>
            <li>Leia e aceite a Política de Não-Retaliação.</li>
            <li>Escolha a categoria que melhor descreve a situação.</li>
            <li>
              Descreva o ocorrido com o máximo de detalhe possível:{' '}
              <strong>
                o que aconteceu, onde, quando, quem estava envolvido, se houve testemunhas, se há
                evidências
              </strong>
              .
            </li>
            <li>Anexe arquivos de apoio, se houver (opcional).</li>
            <li>Decida se quer ser anônimo ou se identificar.</li>
            <li>
              Envie. <strong>Anote o protocolo e a senha exibidos na tela.</strong>
            </li>
          </ol>
          <p>
            A denúncia chega imediatamente ao comitê em formato cifrado. Ninguém fora do comitê tem
            acesso.
          </p>
        </section>

        <section id="depois" className="privacy-section">
          <h3>5. O que acontece depois que você denuncia</h3>
          <p>Cada denúncia passa por um rito formal de apuração, com fases bem definidas:</p>
          <pre
            style={{
              background: 'var(--color-bg-muted, #f8fafc)',
              padding: '0.75rem 1rem',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              overflowX: 'auto',
            }}
          >
            Aberta → Triagem → Instrução → Oitivas → Avaliação → Decisão → Encerramento
          </pre>
          <ol>
            <li>
              <strong>Triagem</strong> — Comitê verifica se a denúncia tem elementos mínimos para
              apuração.
            </li>
            <li>
              <strong>Instrução</strong> — Coleta de documentos, evidências, contextualização.
            </li>
            <li>
              <strong>Oitivas</strong> — Quando aplicável, são ouvidas testemunhas e a pessoa
              indicada.
            </li>
            <li>
              <strong>Avaliação</strong> — Comitê delibera sobre o caso. Esta deliberação{' '}
              <strong>sempre inclui ampla defesa</strong>: ninguém é punido sem ser ouvido.
            </li>
            <li>
              <strong>Decisão</strong> — Comitê toma a decisão final, justificada por escrito.
            </li>
            <li>
              <strong>Encerramento</strong> — Caso é encerrado, com registro auditável e imutável.
            </li>
          </ol>
          <p>
            A qualquer momento você pode acompanhar o andamento no mesmo canal, em "Acompanhar
            denúncia", usando o protocolo e a frase-senha que recebeu.
          </p>
          <h4 style={{ marginTop: '1rem', fontSize: '1rem' }}>Quem compõe o comitê</h4>
          <p>Titulares:</p>
          <ul>
            <li>
              <strong>Jéssica Frazão</strong> — SESMT
            </li>
            <li>
              <strong>Fabiana Carvalho</strong> — Recursos Humanos
            </li>
            <li>
              <strong>Vinícius Paulino do Nascimento</strong> — Compras
            </li>
          </ul>
          <p>
            Suplentes: <strong>Isabella Rennow</strong>, <strong>Robson Nunes</strong> e{' '}
            <strong>Rebeca Silva</strong>.
          </p>
          <p>O comitê delibera coletivamente. Nenhuma decisão é tomada por uma única pessoa.</p>
          <p>
            O comitê usa uma ferramenta de <strong>inteligência artificial como apoio</strong>: ela
            organiza informações e aponta pendências do processo. Ela{' '}
            <strong>não vota e não decide</strong> — toda decisão é tomada pelas pessoas acima.
          </p>
          <h4 style={{ marginTop: '1rem', fontSize: '1rem' }}>Quanto tempo demora</h4>
          <ul>
            <li>
              Primeiro retorno: <strong>até 5 dias úteis</strong> após o recebimento.
            </li>
            <li>
              Conclusão da apuração: <strong>até 60 dias úteis</strong>, prorrogáveis uma única vez
              por mais 30 dias em casos complexos, com justificativa registrada.
            </li>
          </ul>
        </section>

        <section id="recurso" className="privacy-section">
          <h3>6. Seu direito de recurso</h3>
          <p>
            Se você não concordar com a decisão final, tem direito a apresentar{' '}
            <strong>recurso</strong>, que será analisado por colegiado distinto do que decidiu o
            caso original. Prazo: <strong>15 dias corridos</strong> a partir da comunicação da
            decisão.
          </p>
          <p>O recurso é apresentado pelo mesmo canal, usando seu protocolo.</p>
        </section>

        <section id="retaliacao" className="privacy-section">
          <h3>7. Proteção contra retaliação</h3>
          <p>
            A Política de Não-Retaliação (LGP-02) garante que você não sofrerá nenhuma represália
            por denunciar de boa-fé. Isto vale mesmo que a apuração conclua que a denúncia é
            improcedente.
          </p>
          <p>
            <strong>A boa-fé é presumida.</strong> Você só perde a proteção se ficar comprovado, com
            prova robusta, que você sabia que a denúncia era falsa e agiu para prejudicar alguém
            deliberadamente.
          </p>
          <p>
            Se você sofrer ou suspeitar de retaliação, <strong>denuncie novamente</strong> usando a
            categoria "Retaliação". O comitê tratará com prioridade máxima.
          </p>
        </section>

        <section id="confidencialidade" className="privacy-section">
          <h3>8. Confidencialidade</h3>
          <p>
            Todos os membros do comitê assumem dever formal de sigilo. As informações de cada
            denúncia são acessadas apenas pelo comitê, em sistema cifrado, com cada acesso
            registrado em log imutável. Vazamento de informação por membro do comitê é falta grave.
          </p>
        </section>

        <section id="faq" className="privacy-section">
          <h3>9. Perguntas frequentes</h3>
          <dl>
            <dt>
              <strong>E se a denúncia for contra meu próprio chefe?</strong>
            </dt>
            <dd>
              O sistema não revela quem você é. Seu chefe não tem como saber, nem mesmo perguntando
              à empresa. Se houver mudança no seu tratamento após a denúncia, isso é retaliação e
              deve ser denunciada novamente.
            </dd>
            <dt>
              <strong>E se eu denunciar algo que depois descobrir que não era verdade?</strong>
            </dt>
            <dd>
              Sem problema. A política protege quem age de boa-fé. Só perde a proteção quem mente
              deliberadamente para prejudicar alguém.
            </dd>
            <dt>
              <strong>Posso anexar áudios, prints de conversa ou fotos?</strong>
            </dt>
            <dd>Sim. Tudo o que você anexar é cifrado e fica acessível apenas ao comitê.</dd>
            <dt>
              <strong>Posso denunciar em nome de outra pessoa?</strong>
            </dt>
            <dd>
              Sim, mas o ideal é que a própria pessoa atingida denuncie. Se não for possível (por
              exemplo, ela tem medo), você pode denunciar e o comitê fará o que conseguir com o que
              você relatar.
            </dd>
            <dt>
              <strong>Como sei que a empresa vai levar a sério?</strong>
            </dt>
            <dd>
              O sistema registra cada ação do comitê em log auditável e imutável. Há prazos claros e
              direito de recurso. A Lei 14.457/2022 e a Lei 12.846/2013 obrigam a empresa a tratar
              denúncias com seriedade — e fiscalizam isso.
            </dd>
            <dt>
              <strong>Quem cuida do canal tecnicamente?</strong>
            </dt>
            <dd>
              A plataforma é interna da Contato Facilites, mas foi projetada para que nem a equipe
              técnica consiga ler conteúdo de denúncia. A criptografia é ponta-a-ponta entre o
              denunciante e o comitê.
            </dd>
            <dt>
              <strong>Quem pode utilizar o Canal de Denúncias?</strong>
            </dt>
            <dd>O canal é aberto a colaboradores, clientes, fornecedores e ao público em geral.</dd>
            <dt>
              <strong>Qual é o horário de funcionamento do canal?</strong>
            </dt>
            <dd>Ele está disponível 24 horas por dia, 7 dias por semana.</dd>
            <dt>
              <strong>Como faço para acessar e registrar um relato?</strong>
            </dt>
            <dd>
              Você pode acessar o canal apontando a câmera do celular para o QR Code disponibilizado
              pela empresa ou digitando o endereço{' '}
              <strong>https://canal-contato-facilites.pages.dev/</strong>.
            </dd>
            <dt>
              <strong>Preciso me identificar para fazer uma denúncia?</strong>
            </dt>
            <dd>
              Não, o canal permite que o seu relato seja feito de forma anônima ou identificada.
              Como o anonimato é garantido tecnicamente, veja a seção 3.
            </dd>
            <dt>
              <strong>Posso sofrer alguma punição ou perseguição por denunciar?</strong>
            </dt>
            <dd>
              Não, a empresa incentiva o uso do canal para um relato seguro e sem retaliação. A
              proteção está detalhada na seção 7.
            </dd>
            <dt>
              <strong>Quais atitudes são consideradas infrações e devem ser denunciadas?</strong>
            </dt>
            <dd>
              Devem ser denunciados comportamentos suspeitos, pressões para desvios de conduta,
              violações à Lei Anticorrupção, infrações à LGPD ou qualquer forma de trabalho infantil
              e análogo ao escravo.
            </dd>
            <dt>
              <strong>O que devo fazer caso identifique um conflito de interesses?</strong>
            </dt>
            <dd>
              Qualquer relação pessoal ou financeira entre colaboradores de fornecedores e da
              CONTATO que possa influenciar decisões comerciais deve ser informada imediatamente.
            </dd>
            <dt>
              <strong>
                Quais são as regras para aceitar ou oferecer brindes em relações comerciais
                privadas?
              </strong>
            </dt>
            <dd>
              O limite de valor para presentes e entretenimento é de 1/20 do salário mínimo para
              entidades privadas. A exceção fica apenas para brindes institucionais sem valor
              comercial expressivo, como canetas, cadernos e calendários com o logotipo da empresa.
            </dd>
            <dt>
              <strong>
                Quais são as regras de brindes aplicadas especificamente a fornecedores?
              </strong>
            </dt>
            <dd>
              Para os fornecedores, não é permitido oferecer presentes, entretenimento ou favores
              que excedam o limite de 1/20 (um vinte avos) do salário mínimo. Para que exceções
              sejam aceitas nessas transações, é obrigatória uma solicitação por escrito e assinada
              digitalmente pelo responsável legal.
            </dd>
            <dt>
              <strong>Posso oferecer presentes ou vantagens durante uma licitação?</strong>
            </dt>
            <dd>
              Não, é estritamente proibido oferecer presentes durante processos licitatórios ou de
              renovação contratual.
            </dd>
            <dt>
              <strong>É permitido oferecer presentes ou refeições a agentes públicos?</strong>
            </dt>
            <dd>
              Não, a empresa adota tolerância zero, sendo terminantemente proibido oferecer qualquer
              vantagem indevida, presente, refeição ou entretenimento a agentes públicos municipais,
              estaduais ou federais.
            </dd>
          </dl>
        </section>
      </article>
    </div>
  );
}
